import { toast } from "react-toastify";
import {
  CopyAgenticLogicRuleRequestBodyType,
  CopyAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types/functions/agentic-logic/rules/copy";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

/**
 * Fork a rule's current workspace into a rule of its own. The source travels
 * server-side — the body carries only the copy's name and description, so the
 * caller cannot save one rule's code under another rule's name.
 */
const copyAgenticLogicRule = async (
  payload: ApiRequest<CopyAgenticLogicRuleRequestBodyType, { ruleId: string }>
) => {
  const resp = await api.post<
    CopyAgenticLogicRuleResponseBodyType,
    CopyAgenticLogicRuleRequestBodyType
  >(`/v2/agentic-logic/rules/${payload.pathParams.ruleId}/copy`, payload.body);
  return resp.data;
};

export const useCopyAgenticLogicRule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CopyAgenticLogicRuleResponseBodyType,
    Error,
    ApiRequest<CopyAgenticLogicRuleRequestBodyType, { ruleId: string }>,
    unknown
  >({
    mutationFn: async (data) => copyAgenticLogicRule(data),
    onSuccess: async (_data, request) => {
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.rules(),
        }),
      ];
      if (request.body.discardSourceDraft) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: agenticLogicKeys.rule(request.pathParams.ruleId),
          }),
          queryClient.invalidateQueries({
            queryKey: agenticLogicKeys.draft(request.pathParams.ruleId),
          })
        );
      }
      await Promise.all(invalidations);
    },
    onError: () => {
      toast.error(
        "We could not save this copy. Check the message in the copy dialog."
      );
    },
  });
};
